import type {
  CreateSupportIssueInput,
  DestekMe,
  FormField,
  ListSupportIssuesResponse,
  SupportIssueComment,
  SupportIssueDetail,
  SupportIssuePriority,
  SupportIssueStatus,
  SupportIssueType,
} from '@mbbe/types';
import { destekApiFetch } from './destekApiClient';

export function getMe(): Promise<DestekMe> {
  return destekApiFetch('/destek/me');
}

export interface DestekProject {
  id: string;
  name: string;
  description: string | null;
  status: 'active' | 'archived';
  fieldCount: number;
  createdAt: string;
}

export function getProjects(): Promise<{ items: DestekProject[] }> {
  return destekApiFetch('/destek/projects');
}

// type verilirse alanlar seçili issue türüne göre filtrelenir (issueType NULL = tüm türler).
export function getProjectFields(
  projectId: string,
  type?: SupportIssueType
): Promise<{ fields: FormField[] }> {
  const qs = type ? `?type=${encodeURIComponent(type)}` : '';
  return destekApiFetch(`/destek/projects/${projectId}/fields${qs}`);
}

export interface ListMyIssuesParams {
  status?: SupportIssueStatus | 'all';
  priority?: SupportIssuePriority | 'all';
  type?: SupportIssueType | 'all';
  q?: string;
  limit?: number;
  offset?: number;
}

export function listMyIssues(params: ListMyIssuesParams = {}): Promise<ListSupportIssuesResponse> {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.priority && params.priority !== 'all') qs.set('priority', params.priority);
  if (params.type && params.type !== 'all') qs.set('type', params.type);
  if (params.q) qs.set('q', params.q);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const query = qs.toString();
  return destekApiFetch(`/destek/issues${query ? `?${query}` : ''}`);
}

export function getMyIssue(id: string): Promise<SupportIssueDetail> {
  return destekApiFetch(`/destek/issues/${id}`);
}

export function createIssue(body: CreateSupportIssueInput): Promise<SupportIssueDetail> {
  return destekApiFetch('/destek/issues', { method: 'POST', body: JSON.stringify(body) });
}

export function addComment(id: string, body: string, attachments: Array<{key:string;filename:string;size:number;mimeType:string;kind:'image'|'file'}> = []): Promise<SupportIssueComment> {
  return destekApiFetch(`/destek/issues/${id}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body, attachments }),
  });
}
export const presignCommentAttachment=(id:string,body:{sessionId:string;filename:string;fileType:string;fileSize:number;kind:'image'|'file'}):Promise<{uploadUrl:string;key:string}>=>destekApiFetch(`/destek/issues/${id}/comment-attachments/presign`,{method:'POST',body:JSON.stringify(body)});
export const downloadCommentAttachment=(path:string,...init:[init?:{signal?:AbortSignal}]):Promise<{downloadUrl:string;expiresInSeconds:number}>=>destekApiFetch(path.replace(/^\/api/,''),...init);

export function closeIssue(id: string): Promise<SupportIssueDetail> {
  return destekApiFetch(`/destek/issues/${id}/close`, { method: 'POST' });
}

export function reopenIssue(id: string, body: string): Promise<SupportIssueDetail> {
  return destekApiFetch(`/destek/issues/${id}/reopen-request`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export interface PresignResult {
  uploadUrl: string;
  key: string;
  imageId: string;
  sessionId: string;
}

export function presignImage(
  sessionId: string,
  filename: string,
  fileType: string,
  fileSize: number,
  projectId?: string | null,
): Promise<PresignResult> {
  const scopedProjectId = projectId || null;
  return destekApiFetch('/destek/issues/presign-temp', {
    method: 'POST',
    body: JSON.stringify({ sessionId, projectId: scopedProjectId, filename, fileType, fileSize }),
  });
}

export function presignFile(body: {
  sessionId: string;
  projectId: string;
  issueType: SupportIssueType;
  fieldId: string;
  filename: string;
  fileType: string;
  fileSize: number;
}): Promise<{ uploadUrl: string; key: string; fileId: string; sessionId: string }> {
  return destekApiFetch('/destek/issues/file-presign-temp', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteTempImage(key: string): Promise<void> {
  return destekApiFetch('/destek/issues/temp-image', {
    method: 'DELETE',
    body: JSON.stringify({ key }),
  });
}

export function reportUploadError(body: {
  uploadType: 'support-image' | 'support-file';
  phase?: 'presign' | 'storage';
  fileType?: string;
  fileSize?: number;
  status?: number;
  statusText?: string;
  responseSnippet?: string;
  keyPrefix?: string;
  origin?: string;
}): Promise<{ ok: boolean }> {
  return destekApiFetch('/destek/issues/upload-error', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateMyLanguage(language: 'tr' | 'en'): Promise<{ success: boolean; language: 'tr' | 'en' }> {
  return destekApiFetch('/destek/me/language', {
    method: 'PUT',
    body: JSON.stringify({ language }),
  });
}
